import {
  isIOS as isIOSDevice,
  osVersion as iosOSVersion,
} from "react-device-detect";
import semver from "semver";

/**
 * Detect iOS (iPhone, iPad, iPod).
 * Also detects iPad on iOS 13+ which reports as MacIntel.
 *
 * Note: iOS Safari doesn't support programmatic volume control
 * (volume property is read-only and always returns 1) and uses
 * different fullscreen methods on video elements.
 */
export const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

/**
 * Detect Safari browser (not Chrome/Edge which also include "Safari" in UA).
 */
export const isSafari = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
};

/**
 * Detect Firefox browser (desktop and iOS Firefox).
 */
export const isFirefox = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Firefox|FxiOS/.test(ua);
};

/**
 * Get Safari version from user agent.
 * Returns null if not Safari.
 */
export const getSafariVersion = (): number | null => {
  if (!isSafari()) return null;
  const match = navigator.userAgent.match(/Version\/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
};

/**
 * WebKit < 18.4 does not support audiooutput enumeration.
 * This affects Safari < 18.4 on any platform, and ALL browsers on iOS < 18.4
 * (since Apple requires all iOS browsers to use WebKit).
 * See: https://webkit.org/blog/16574/webkit-features-in-safari-18-4/
 */
export const isWebKitWithoutAudioOutputSupport = (): boolean => {
  const safariVersion = getSafariVersion();
  if (safariVersion !== null) {
    return safariVersion < 18.4;
  }
  const coercedIOSVersion = isIOSDevice ? semver.coerce(iosOSVersion) : null;
  if (coercedIOSVersion) {
    return semver.lt(coercedIOSVersion, "18.4.0");
  }
  return false;
};
